<script lang="ts">
    const component = { name: 'Menu_SendLog' };
    export default { name: component.name };
</script>

<script lang="ts" setup>
    import { ref, computed, watch, inject } from 'vue';
    import vm from '@journeys/Journeys.viewmodel';
    import MultiSelect from '@shared/components/MultiSelect/MultiSelect.vue';
    import FormCheckable from '@shared/components/Form/FormCheckable.vue';
    import { SwitchStyle } from '@shared/components/Form/Form';
    import Modal from '@shared/components/Modal.vue';
    import List from '@shared/components/List/List.vue';
    import BsAlert from '@shared/components/BsAlert.vue';
    import { AlertTypes } from '@shared/components/BsAlerts';
    import Preview from '@shared/components/Preview.vue';
    import { GetJourneySendLog, GetSendOccasionRecipients, GetJourneySmsLog, GetSmsMessageRecipients } from '@journeys/Journey.access';
    import type { IssueSendOccasionLogItem, IssueSendOccasionRecipientListItem, SmsMessageLogItem, SmsMessageRecipientListItem } from '@journeys/journeys';
    import { formatDateTime } from '@shared/utils/formatDateTime';
    import { type dateFilter } from '@shared/components/DateRangePicker';
    import { injectorAccessInjectionKey } from '@cwd/lib/provider-keys';
    import { ContextAccess } from '@shared/Root/Context.access';
    import { combineUrlSegments } from '@shared/utils/combineUrlSegments';

    const { activeJourney, editor } = vm;

    const injectorAccess = inject(injectorAccessInjectionKey)!;
    const contextAccess: ContextAccess = injectorAccess.injector.get(ContextAccess);

    // ── filter state ──────────────────────────────────────────────
    const filterIssueId = ref<string | null>(editor.state.sendLogIssueFilter);
    const filterSmsMessageId = ref<string | null>(null);
    const listDateFilter = ref<dateFilter>({ start: null, end: null });
    const hideEmpty = ref(false);

    // tab state: switches the shared List between mailings and SMS
    const currentTab = ref<'email' | 'sms'>('email');
    // When the filter narrows the log to a single issue or a single SMS message, the other tab is redundant
    const tabs = computed(() => ({
        email: { name: 'email', display: $Resources.JourneySendLogColumnEmail, hidden: !!filterSmsMessageId.value },
        sms: { name: 'sms', display: $Resources.JourneySendLogColumnSms, hidden: !!filterIssueId.value },
    }));

    watch(filterIssueId, (val) => {
        if (val) currentTab.value = 'email';
    });
    watch(filterSmsMessageId, (val) => {
        if (val) currentTab.value = 'sms';
    });

    // data state
    const loading = ref(false);
    const rows = ref<IssueSendOccasionLogItem[]>([]);

    // SMS data state
    const smsLoading = ref(false);
    const smsRows = ref<SmsMessageLogItem[]>([]);
    const highlightedSmsId = ref<string | null>(null);

    // email preview modal state
    const previewUrl = ref<string | null>(null);

    const showPreview = (issueId: string) => {
        previewUrl.value = combineUrlSegments(contextAccess.state.currentAccount!.PublicEventUrl.PublicHost, 'Issues', issueId, '?preview=true');
    };

    // recipient modal state (shared for email and SMS)
    const recipientView = ref(false);
    const recipientViewLoading = ref(false);
    const recipientRows = ref<IssueSendOccasionRecipientListItem[]>([]);
    const recipientTitle = ref('');
    const recipientViewIsSms = ref(false);
    const smsRecipientRows = ref<SmsMessageRecipientListItem[]>([]);

    // ── issue options for dropdown ────────────────────────────────
    const issueOptions = computed(() => {
        const result: Record<string, { IssueId: string; Subject: string }> = {};
        for (const r of rows.value) {
            if (!result[r.Issue.IssueId]) {
                result[r.Issue.IssueId] = { IssueId: r.Issue.IssueId, Subject: r.Issue.Subject || r.Issue.IssueName };
            }
        }
        return result;
    });

    // SMS messages have no parent "Issue" — each message is its own top-level item
    const smsOptions = computed(() => {
        const result: Record<string, { SmsMessageId: string; Subject: string }> = {};
        for (const r of smsRows.value) {
            result[r.SmsMessageId] = { SmsMessageId: r.SmsMessageId, Subject: r.Subject };
        }
        return result;
    });

    // ── combined filter dropdown (email issues + sms messages in one list) ──
    // Keyed by a prefixed id since IssueId/SmsMessageId are separate id spaces.
    const filterOptions = computed(() => {
        const result: Record<string, { Key: string; Subject: string }> = {};
        for (const opt of Object.values(issueOptions.value)) {
            result['issue:' + opt.IssueId] = { Key: 'issue:' + opt.IssueId, Subject: opt.Subject };
        }
        for (const opt of Object.values(smsOptions.value)) {
            result['sms:' + opt.SmsMessageId] = { Key: 'sms:' + opt.SmsMessageId, Subject: opt.Subject };
        }
        return result;
    });

    const filterSelectedIds = computed(() => {
        if (filterIssueId.value) return ['issue:' + filterIssueId.value];
        if (filterSmsMessageId.value) return ['sms:' + filterSmsMessageId.value];
        return [];
    });

    const onFilterSelectionChange = (val: string[]) => {
        const key = val[0] ?? null;
        filterIssueId.value = key?.startsWith('issue:') ? key.slice('issue:'.length) : null;
        filterSmsMessageId.value = key?.startsWith('sms:') ? key.slice('sms:'.length) : null;
        loadSendLog();
    };

    // ── date range ────────────────────────────────────────────────
    // The picker gives local calendar days ('YYYY-MM-DD'); the range covers [start 00:00, end+1 00:00) in the user's timezone.
    const localDay = (date: string, addDays = 0) => {
        const [y, m, d] = date.split('-').map(Number);
        return new Date(y, m - 1, d + addDays);
    };
    const dateRange = computed(() => ({
        from: listDateFilter.value.start ? localDay(listDateFilter.value.start) : null,
        to: listDateFilter.value.end ? localDay(listDateFilter.value.end, 1) : null,
    }));

    // ── fetch ─────────────────────────────────────────────────────
    const loadSendLog = async () => {
        if (!activeJourney.JourneyId) return;
        loading.value = true;
        rows.value = await GetJourneySendLog(activeJourney.JourneyId, {
            issueId: filterIssueId.value ?? undefined,
            from: dateRange.value.from?.toISOString(),
            to: dateRange.value.to?.toISOString(),
        });
        loading.value = false;
    };

    const onDateRangeChange = (start: string, end: string) => {
        listDateFilter.value.start = start || null;
        listDateFilter.value.end = end || null;
        loadSendLog();
    };

    const loadSmsLog = async () => {
        if (!activeJourney.JourneyId) return;
        smsLoading.value = true;
        smsRows.value = await GetJourneySmsLog(activeJourney.JourneyId);
        smsLoading.value = false;
    };

    // Load (or reload) the log data whenever the modal is opened. If the viewmodel pre-set a filter (from a
    // StepCard link) honour it, otherwise reset any filtering left over from a previous time the modal was open —
    // e.g. opening via SidebarMain's plain "log" button must not keep showing a single issue/sms from before.
    watch(
        () => editor.state.showSendLogModal,
        (open) => {
            if (!open) return;

            if (editor.state.sendLogIssueFilter !== null) {
                filterIssueId.value = editor.state.sendLogIssueFilter;
                filterSmsMessageId.value = null;
                highlightedSmsId.value = null;
                currentTab.value = 'email';
            } else if (editor.state.sendLogSmsFilter !== null) {
                filterSmsMessageId.value = editor.state.sendLogSmsFilter;
                highlightedSmsId.value = editor.state.sendLogSmsFilter;
                filterIssueId.value = null;
                currentTab.value = 'sms';
            } else {
                filterIssueId.value = null;
                filterSmsMessageId.value = null;
                highlightedSmsId.value = null;
                currentTab.value = 'email';
            }
            editor.state.sendLogIssueFilter = null;
            editor.state.sendLogSmsFilter = null;

            // Only fetch the tab that will actually be shown; the other tab
            // loads lazily the first time the user switches to it.
            currentTab.value === 'email' ? loadSendLog() : loadSmsLog();
        }
    );

    // Lazily fetch the other tab's data the first time it's viewed.
    watch(currentTab, (tab) => {
        if (!editor.state.showSendLogModal) return;
        if (tab === 'email' && rows.value.length === 0 && !loading.value) loadSendLog();
        if (tab === 'sms' && smsRows.value.length === 0 && !smsLoading.value) loadSmsLog();
    });

    const closeModal = () => {
        recipientView.value = false;
        editor.state.showSendLogModal = false;
    };

    // ── filtered rows (sorting is handled by the shared List component) ──
    const displaySmsRows = computed(() => {
        let list = smsRows.value;
        if (filterSmsMessageId.value) list = list.filter((r) => r.SmsMessageId === filterSmsMessageId.value);
        if (hideEmpty.value) list = list.filter((r) => r.RecipientCount > 0);
        const { from, to } = dateRange.value;
        if (from) list = list.filter((r) => r.Started && new Date(r.Started) >= from);
        if (to) list = list.filter((r) => r.Started && new Date(r.Started) < to);
        return list;
    });

    const displayRows = computed(() => (hideEmpty.value ? rows.value.filter((r) => r.RecipientCount > 0) : rows.value));

    // ── no log found for the issue/sms node the modal was opened from ──
    // With a date filter active, the List (and its date picker) stays visible and shows its own empty state instead.
    const noLogForFilter = computed(() => {
        if (filterIssueId.value) return !loading.value && rows.value.length === 0 && !listDateFilter.value.start && !listDateFilter.value.end;
        if (filterSmsMessageId.value) return !smsLoading.value && !smsRows.value.some((r) => r.SmsMessageId === filterSmsMessageId.value);
        return false;
    });

    // ── List component config ─────────────────────────────────────
    const listLoading = computed(() => (currentTab.value === 'email' ? loading.value : smsLoading.value));
    const listItems = computed(() => (currentTab.value === 'email' ? displayRows.value : displaySmsRows.value));
    const listItemId = computed(() => (currentTab.value === 'email' ? 'IssueSendOccasionId' : 'SmsMessageId'));
    const listSelectedId = computed(() => (currentTab.value === 'sms' ? highlightedSmsId.value : null));

    const listTableHeaders = computed(() =>
        currentTab.value === 'email'
            ? [$Resources.JourneySendLogColumnEmail, $Resources.JourneySendLogColumnDate, $Resources.JourneySendLogColumnCount]
            : [$Resources.JourneySendLogColumnSms, $Resources.JourneySendLogColumnDate, $Resources.JourneySendLogColumnCount]
    );

    const listOrderOptions = computed(() =>
        currentTab.value === 'email'
            ? [
                  { key: 'Created', display: $Resources.JourneySendLogColumnDate },
                  { key: 'Subject', display: $Resources.SubjectLabel },
                  { key: 'RecipientCount', display: $Resources.JourneySendLogColumnCount },
              ]
            : [
                  { key: 'Started', display: $Resources.JourneySendLogColumnDate },
                  { key: 'Subject', display: $Resources.SubjectLabel },
                  { key: 'RecipientCount', display: $Resources.JourneySendLogColumnCount },
              ]
    );

    const listFilterKeys = computed(() => (currentTab.value === 'email' ? ['Issue.Subject', 'Issue.IssueName'] : ['Subject']));

    // ── recipient List component config ────────────────────────────
    const recipientListItems = computed(() => (recipientViewIsSms.value ? smsRecipientRows.value : recipientRows.value));
    const recipientListItemId = computed(() => (recipientViewIsSms.value ? 'SmsRecipientId' : 'IssueRecipientId'));
    const recipientListTableHeaders = computed(() =>
        recipientViewIsSms.value
            ? [$Resources.ContactNameLabel, $Resources.SmsNumberLabel, $Resources.StatusLabel]
            : [$Resources.ContactNameLabel, $Resources.EmailLabel, $Resources.StatusLabel]
    );
    const recipientListFilterKeys = computed(() =>
        recipientViewIsSms.value ? ['Contact.Name', 'SmsNumber', 'StatusName'] : ['Contact.Name', 'Contact.Email', 'StatusName']
    );
    const recipientListOrderOptions = computed(() =>
        recipientViewIsSms.value
            ? [
                  { key: 'Contact.Name', display: $Resources.ContactNameLabel },
                  { key: 'SmsNumber', display: $Resources.SmsNumberLabel },
                  { key: 'StatusName', display: $Resources.StatusLabel },
              ]
            : [
                  { key: 'Contact.Name', display: $Resources.ContactNameLabel },
                  { key: 'Contact.Email', display: $Resources.EmailLabel },
                  { key: 'StatusName', display: $Resources.StatusLabel },
              ]
    );

    // ── recipient modal ───────────────────────────────────────────
    const openRecipients = async (row: IssueSendOccasionLogItem) => {
        if (row.RecipientCount === 0) return;
        recipientTitle.value = formatDateTime(row.Created);
        recipientViewIsSms.value = false;
        recipientView.value = true;
        recipientViewLoading.value = true;
        recipientRows.value = await GetSendOccasionRecipients(row.Issue.IssueId, row.IssueSendOccasionId);
        recipientViewLoading.value = false;
    };

    const openSmsRecipients = async (row: SmsMessageLogItem) => {
        if (row.RecipientCount === 0) return;
        recipientTitle.value = row.Subject;
        recipientViewIsSms.value = true;
        smsRecipientRows.value = [];
        recipientView.value = true;
        recipientViewLoading.value = true;
        smsRecipientRows.value = await GetSmsMessageRecipients(row.SmsMessageId);
        recipientViewLoading.value = false;
    };

    // ── helpers ───────────────────────────────────────────────────
    // IssueStatistics reads the period from ?start=&end= (local 'YYYY-MM-DD')
    const issueStatsUrl = (issueId: string, start: string, end: string) => {
        const params = new URLSearchParams();
        if (start) params.set('start', start);
        if (end) params.set('end', end);
        const qs = params.toString();
        return `/Issues/${issueId}/Statistics${qs ? '?' + qs : ''}`;
    };

    const issueLabel = (row: IssueSendOccasionLogItem) => row.Issue.Subject || row.Issue.IssueName || row.Issue.IssueId;

    // ── row helpers (drive the single #item template for both tabs) ──
    type LogRow = IssueSendOccasionLogItem | SmsMessageLogItem;

    const rowHref = (item: LogRow) =>
        currentTab.value === 'email'
            ? issueStatsUrl((item as IssueSendOccasionLogItem).Issue.IssueId, listDateFilter.value.start || '', listDateFilter.value.end || '')
            : `/textmessages/${(item as SmsMessageLogItem).SmsMessageId}`;

    const rowLabel = (item: LogRow) => (currentTab.value === 'email' ? issueLabel(item as IssueSendOccasionLogItem) : (item as SmsMessageLogItem).Subject);

    const rowDate = (item: LogRow) => {
        if (currentTab.value === 'email') return formatDateTime((item as IssueSendOccasionLogItem).Created);
        const started = (item as SmsMessageLogItem).Started;
        return started ? formatDateTime(started) : '—';
    };

    const openRow = (item: LogRow) =>
        currentTab.value === 'email' ? openRecipients(item as IssueSendOccasionLogItem) : openSmsRecipients(item as SmsMessageLogItem);

    // ── preview (email: iframe of the public rendered issue; sms: plain-text popover) ──
    const openPreview = (item: LogRow) => showPreview((item as IssueSendOccasionLogItem).Issue.IssueId);
    const smsPreviewInfo = (item: LogRow) => {
        const sms = item as SmsMessageLogItem;
        return { heading: sms.Subject || $Resources.NoTitleLabel, body: sms.Body, displayHtml: true };
    };
</script>

<template>
    <Modal :show="editor.state.showSendLogModal" :title="$Resources.JourneySendLogHeading" size="xl" @close="closeModal">
        <template #content>
            <template v-if="recipientView">
                <div class="form-group m-sm-b">
                    <button type="button" class="btn btn-subtle btn-reset" @click="recipientView = false">
                        <span class="glyphicon glyphicon-chevron-left m-xxs-r c-light"></span>
                        {{ $Resources.BackButton }}
                    </button>
                </div>

                <h3 class="m-lg-b">{{ recipientTitle }}</h3>

                <List
                    class="th3-text-right"
                    displayAs="table"
                    :items="recipientListItems"
                    :itemId="recipientListItemId"
                    :tableHeaders="recipientListTableHeaders"
                    :filterKeys="recipientListFilterKeys"
                    :orderOptions="recipientListOrderOptions"
                    :loading="recipientViewLoading">
                    <template #item="{ item }">
                        <td>
                            <a :href="`/Contact/${item.Contact.ContactId}`" target="_blank" rel="noopener">
                                {{ item.Contact.Name }}
                                <span class="glyphicon glyphicon glyphicon-new-window c-light m-xxs-l" aria-hidden="true"></span>
                            </a>
                        </td>
                        <td>{{ recipientViewIsSms ? item.SmsNumber : item.Contact.Email }}</td>
                        <td class="text-right">{{ item.StatusName }}</td>
                    </template>
                </List>
            </template>

            <template v-else-if="noLogForFilter">
                <BsAlert :type="AlertTypes.Info" :content="$Resources.JourneySendLogNoDataMessage" :dismissible="false" />
            </template>

            <template v-else>
                <!-- Filter: issue / sms message (combined) -->
                <MultiSelect
                    class="form-group max-700"
                    :items="filterOptions"
                    :selectedItems="filterSelectedIds"
                    @update:selectedItems="onFilterSelectionChange"
                    displayProp="Subject"
                    selectProp="Key"
                    selectOnly="Key"
                    :maxSelectable="1"
                    :label="$Resources.JourneySendLogFilterLabel" />

                <List
                    class="th3-text-right"
                    displayAs="table"
                    :tabs="tabs"
                    :currentTab="currentTab"
                    :items="listItems"
                    :itemId="listItemId"
                    :tableHeaders="listTableHeaders"
                    :orderOptions="listOrderOptions"
                    :loading="listLoading"
                    :filterKeys="listFilterKeys"
                    :selectedId="listSelectedId"
                    :dateFilter="listDateFilter"
                    @update:tab="(newTab: string) => (currentTab = newTab as 'email' | 'sms')"
                    @onDateRangeChange="onDateRangeChange">
                    <template #toolbar>
                        <!-- Toggle: hide empty -->
                        <FormCheckable
                            class="form-group m-sm-t m-sm-r"
                            :switchStyle="SwitchStyle.Standard"
                            :label="$Resources.JourneySendLogHideEmptyLabel"
                            :checked="hideEmpty"
                            @update:modelValue="(v: boolean) => (hideEmpty = v)" />
                    </template>
                    <template #item="{ item }">
                        <td>
                            <a :href="rowHref(item)" target="_blank" rel="noopener">
                                {{ rowLabel(item) }}
                                <span class="glyphicon glyphicon glyphicon-new-window c-light m-xxs-l" aria-hidden="true"></span>
                            </a>
                            <button
                                v-if="currentTab === 'email'"
                                type="button"
                                class="btn btn-subtle btn-sm m-xs-l"
                                v-tooltip.top="$Resources.IssuePreviewButton"
                                @click="openPreview(item)">
                                <span class="glyphicon glyphicon-eye-open" aria-hidden="true"></span>
                                <span class="sr-only">{{ $Resources.IssuePreviewButton }}</span>
                            </button>
                            <button v-else type="button" class="btn btn-subtle btn-sm m-xs-l" v-popover-info.top="smsPreviewInfo(item)">
                                <span class="glyphicon glyphicon-eye-open" aria-hidden="true"></span>
                                <span class="sr-only">{{ $Resources.IssuePreviewButton }}</span>
                            </button>
                        </td>
                        <td>{{ rowDate(item) }}</td>
                        <td class="text-right">
                            <button v-if="item.RecipientCount > 0" class="btn-reset btn btn-link" @click="openRow(item)">
                                {{ item.RecipientCount }}
                            </button>
                            <span v-else>0</span>
                        </td>
                    </template>
                </List>
            </template>
        </template>
    </Modal>

    <Preview :previewUrl="previewUrl" @close="previewUrl = null" />
</template>
